import React, { useState, forwardRef, useImperativeHandle } from "react";
import { useNavigate } from "react-router-dom";
import { giroTechLogo } from "../../utils/constants";
import UserCreateForm from "./forms/UserCreateForm";
import CourseForm from "./forms/CourseForm";
import GroupForm from "./forms/GroupForm";
import CoursesTab from "./tabs/CoursesTab";
import UsersTab from "./tabs/UsersTab";
import CertificatesTab from "./tabs/CertificatesTab";
import AnalyticsTab from "./tabs/AnalyticsTab";
import GroupsTab from "./tabs/GroupsTab";
import ProgressTab from "./tabs/ProgressTab";
import TabButton from "./components/TabButton";
import CSVImportModal from "./components/CSVImportModal";
import ErrorMessage from "./components/ErrorMessage";
import useAdminData from "../../hooks/useAdminData";
import useAdminActions from "../../hooks/useAdminActions";

const AdminDashboard = forwardRef(({ user, onLogout, onCoursesChange }, ref) => {
  const [activeTab, setActiveTab] = useState("courses");
  const navigate = useNavigate();

  // Use custom hooks
  const adminData = useAdminData(user);
  const adminActions = useAdminActions(user, onCoursesChange, () => {
    // Refresh all data
    adminData.fetchUsers();
    adminData.fetchGroups();
    adminData.fetchCertificates();
    adminData.fetchVideoAccess();
    adminData.fetchCourseProgress();
    adminData.fetchAdminCourses();
  });

  // Expose refreshData method to parent component
  useImperativeHandle(ref, () => ({
    refreshData: () => {
      adminData.fetchUsers();
      adminData.fetchGroups();
      adminData.fetchCertificates();
      adminData.fetchVideoAccess();
      adminData.fetchCourseProgress();
      adminData.fetchAdminCourses();
    }
  }));

  // Handle view user dashboard
  const handleViewUserDashboard = (user) => {
    navigate(`/admin/users/${user.id}/dashboard`);
  };

  // Handle update user wrapper
  const handleUpdateUserWrapper = async (userData) => {
    if (adminActions.editingUser) {
      return await adminActions.handleUpdateUser(adminActions.editingUser.id, userData);
    }
    throw new Error("Nenhum usuário sendo editado");
  };

  // Handle update group wrapper
  const handleUpdateGroupWrapper = async (groupData) => {
    if (adminActions.editingGroup) {
      return await adminActions.handleUpdateGroup(adminActions.editingGroup.id, groupData);
    }
    throw new Error("Nenhum grupo sendo editado");
  };

  // Handle update course wrapper
  const handleUpdateCourseWrapper = async (courseData) => {
    if (adminActions.editingCourse) {
      return await adminActions.handleUpdateCourse(adminActions.editingCourse.id, courseData);
    }
    throw new Error("Nenhum curso sendo editado");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Nunca";
    return new Date(dateString).toLocaleString("pt-BR");
  };

  // Render functions for each tab
  const renderCoursesTab = () => (
    <CoursesTab
      courses={adminData.courses}
      onShowCreateForm={() => adminActions.setShowCreateForm(true)}
      onEditCourse={adminActions.handleEditCourse}
      onDeleteCourse={adminActions.handleDeleteCourse}
      onCoursesChange={onCoursesChange}
    />
  );

  const renderUsersTab = () => (
    <UsersTab
      users={adminData.users}
      usersPagination={adminData.usersPagination}
      loading={adminData.loading}
      error={adminData.error}
      onEditUser={adminActions.handleEditUser}
      onViewUserDashboard={handleViewUserDashboard}
      onExportCSV={adminActions.exportUsersCSV}
      onShowCSVImport={() => adminActions.setShowCSVImport(true)}
      onShowUserForm={() => adminActions.setShowUserForm(true)}
      formatDate={formatDate}
      userSearchTerm=""
      setUserSearchTerm={() => {}}
      userSearchQuery=""
      onUserSearch={() => {}}
      onClearUserSearch={() => {}}
      onSearchKeyPress={() => {}}
    />
  );

  const renderGroupsTab = () => (
    <GroupsTab
      groups={adminData.groups}
      loading={adminData.loading}
      error={adminData.error}
      onEditGroup={adminActions.handleEditGroup}
      onDeleteGroup={adminActions.handleDeleteGroup}
      onShowGroupForm={() => adminActions.setShowGroupForm(true)}
      formatDate={formatDate}
      groupSearchTerm=""
      setGroupSearchTerm={() => {}}
      groupDomain=""
      setGroupDomain={() => {}}
      onOpenGroupForm={(group) => adminActions.handleEditGroup(group)}
    />
  );

  const renderCertificatesTab = () => (
    <CertificatesTab
      certificates={adminData.certificates}
      loading={adminData.loading}
      error={adminData.error}
      formatDate={formatDate}
      certificatesPagination={adminData.certificatesPagination}
      certificateSearchTerm={adminData.certificateSearchTerm}
      setCertificateSearchTerm={adminData.setCertificateSearchTerm}
      onFetchCertificates={adminData.fetchCertificates}
    />
  );

  const renderAnalyticsTab = () => (
    <AnalyticsTab
      videoAccess={adminData.videoAccess}
      loading={adminData.loading}
      error={adminData.error}
      videoAccessPagination={adminData.videoAccessPagination}
      onFetchVideoAccess={adminData.fetchVideoAccess}
      formatDate={formatDate}
      analyticsSearchTerm={adminData.analyticsSearchTerm}
      setAnalyticsSearchTerm={adminData.setAnalyticsSearchTerm}
    />
  );

  const renderProgressTab = () => (
    <ProgressTab
      courseProgressData={adminData.courseProgressData}
      courseProgressPagination={adminData.courseProgressPagination}
      availableCourses={adminData.availableCourses}
      loading={adminData.loading}
      error={adminData.error}
      onCourseFilterChange={(field, value) => {
        adminData.setProgressFilters(prev => ({ ...prev, [field]: value }));
      }}
      onUserFilterChange={(field, value) => {
        adminData.setProgressFilters(prev => ({ ...prev, [field]: value }));
      }}
      onPageChange={(page) => adminData.fetchCourseProgress(page, adminData.progressFilters.course_filter, adminData.progressFilters.user_filter)}
      onExportCSV={() => adminActions.exportCourseProgressCSV(adminData.progressFilters.course_filter, adminData.progressFilters.user_filter)}
      progressFilters={adminData.progressFilters}
      onProgressFilterChange={(field, value) => {
        adminData.setProgressFilters(prev => ({ ...prev, [field]: value }));
      }}
      onClearProgressFilters={() => {
        adminData.setProgressFilters({ course_filter: "", user_filter: "" });
        adminData.fetchCourseProgress();
      }}
      onFetchCourseProgress={adminData.fetchCourseProgress}
      onExportCourseProgressCSV={() => {
        adminActions.exportCourseProgressCSV(adminData.progressFilters.course_filter, adminData.progressFilters.user_filter);
      }}
      formatDate={formatDate}
    />
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "courses":
        return renderCoursesTab();
      case "users":
        return renderUsersTab();
      case "groups":
        return renderGroupsTab();
      case "certificates":
        return renderCertificatesTab();
      case "analytics":
        return renderAnalyticsTab();
      case "progress":
        return renderProgressTab();
      default:
        return renderCoursesTab();
    }
  };

  return (
    <div>
      {/* Main Content - Dashboard Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Cursos Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 p-3 rounded-lg mr-4">
                <span className="text-2xl">📚</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Cursos</h3>
                <p className="text-sm text-gray-600">Gerenciar cursos e trilhas</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/admin/courses")}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
            >
              Acessar Cursos
            </button>
          </div>

          {/* Usuários Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center mb-4">
              <div className="bg-green-100 p-3 rounded-lg mr-4">
                <span className="text-2xl">👥</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Usuários</h3>
                <p className="text-sm text-gray-600">Gerenciar usuários</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/admin/users")}
              className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
            >
              Acessar Usuários
            </button>
          </div>

          {/* Grupos Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center mb-4">
              <div className="bg-purple-100 p-3 rounded-lg mr-4">
                <span className="text-2xl">🏢</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Grupos</h3>
                <p className="text-sm text-gray-600">Gerenciar grupos</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/admin/groups")}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
            >
              Acessar Grupos
            </button>
          </div>

          {/* Certificados Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center mb-4">
              <div className="bg-yellow-100 p-3 rounded-lg mr-4">
                <span className="text-2xl">🏆</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Certificados</h3>
                <p className="text-sm text-gray-600">Visualizar certificados</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/admin/certificates")}
              className="w-full bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
            >
              Acessar Certificados
            </button>
          </div>

          {/* Analytics Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center mb-4">
              <div className="bg-indigo-100 p-3 rounded-lg mr-4">
                <span className="text-2xl">📊</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Analytics</h3>
                <p className="text-sm text-gray-600">Relatórios de acesso</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/admin/analytics")}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
            >
              Acessar Analytics
            </button>
          </div>

          {/* Progresso Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center mb-4">
              <div className="bg-pink-100 p-3 rounded-lg mr-4">
                <span className="text-2xl">📈</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Progresso</h3>
                <p className="text-sm text-gray-600">Acompanhar progresso</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/admin/progress")}
              className="w-full bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
            >
              Acessar Progresso
            </button>
          </div>
        </div>
      </div>

      {/* Modals and Forms */}
      {adminActions.showCreateForm && (
        <CourseForm
          user={user}
          onClose={() => adminActions.setShowCreateForm(false)}
          onSubmit={adminActions.handleCreateCourse}
        />
      )}

      {adminActions.editingCourse && (
        <CourseForm
          user={user}
          course={adminActions.editingCourse}
          onClose={() => adminActions.setEditingCourse(null)}
          onSubmit={handleUpdateCourseWrapper}
        />
      )}

      {adminActions.showUserForm && (
        <UserCreateForm
          onClose={() => {
            adminActions.setShowUserForm(false);
            adminActions.setEditingUser(null);
          }}
          onSubmit={adminActions.editingUser ? handleUpdateUserWrapper : adminActions.handleCreateUser}
          user={adminActions.editingUser}
        />
      )}

      {adminActions.showGroupForm && (
        <GroupForm
          onClose={() => {
            adminActions.setShowGroupForm(false);
            adminActions.setEditingGroup(null);
          }}
          onSubmit={adminActions.editingGroup ? handleUpdateGroupWrapper : adminActions.handleCreateGroup}
          group={adminActions.editingGroup}
          users={adminData.users}
        />
      )}

      {adminActions.showCSVImport && (
        <CSVImportModal
          onClose={() => adminActions.setShowCSVImport(false)}
          onSubmit={adminActions.importUsersCSV}
        />
      )}

      {/* Error Message */}
      {adminActions.error && (
        <ErrorMessage
          error={adminActions.error}
          onClose={() => adminActions.setError("")}
        />
      )}
    </div>
  );
});

export default AdminDashboard;